import { isEqual } from 'lodash';
import React, { useEffect, useState } from 'react';
import {
  DayPickerButton,
  DAY_END,
  DAY_START,
  DEFAULT_START_HOUR,
  DEFAULT_STOP_HOUR,
  PickerProps,
  WEEK_DAYS,
  WorkHours,
} from '../WeekDayPicker/WeekDayPicker';
import '../WeekDayPicker/WeekDayPicker.pcss';
import './CombinedPicker.pcss';
import { BusinessHoursByDay } from '@mm/state/model/ChatbotModel';
import { TimePicker } from '../../../../../../../common/components/TimePicker/TimePicker';
import RadioInput from '../../../../../../../common/components/RadioInput';
import CheckBox from '../../../../../../../common/components/Checkbox';

export const CombinedPicker = ({
  daysData,
  setDaysData,
  useCheckboxStyle,
}: PickerProps) => {
  const firstEntry = Object.entries(daysData)[0];
  const storedStartHour = firstEntry?.[1].startHour;
  const storedStopHour = firstEntry?.[1].stopHour;

  const storedDays = Object.keys(daysData);

  const [days, setDays] = useState<Set<number>>(
    storedDays.length ? new Set(storedDays.map(d => Number(d))) : new Set([]),
  );

  const [workHours, setWorkHours] = useState<WorkHours>(
    !(storedStartHour || storedStopHour) ||
      (storedStartHour === DAY_START && storedStopHour === DAY_END)
      ? WorkHours.FullDay
      : WorkHours.Hours,
  );

  const isFullDay = workHours === WorkHours.FullDay;

  const areAllChecked = days.size === 7;

  const [startHour, setStartHour] = useState(
    storedStartHour || DEFAULT_START_HOUR,
  );

  const [stopHour, setStopHour] = useState(storedStopHour || DEFAULT_STOP_HOUR);

  const handleToggle = (idx: number) => {
    if (days.has(idx)) {
      setDays(new Set([...days].filter(v => v !== idx)));
      return;
    }
    setDays(new Set([...days.add(idx)]));
  };

  const onSelectFullDay = () => {
    setWorkHours(WorkHours.FullDay);
    setStartHour(DAY_START);
    setStopHour(DAY_END);
  };

  const onSelectByDay = () => {
    setWorkHours(WorkHours.Hours);
    setStartHour(DEFAULT_START_HOUR);
    setStopHour(DEFAULT_STOP_HOUR);
  };

  const safelySetStartTime = (value: string) => {
    const valueFormatted = value;
    if (valueFormatted !== stopHour) {
      setStartHour(valueFormatted);
    } else {
      setStartHour(stopHour);
    }
  };

  const safelySetEndTime = (value: string) => {
    const valueFormatted = value;
    if (valueFormatted !== startHour) {
      setStopHour(valueFormatted);
    } else {
      setStopHour(startHour);
    }
  };

  useEffect(() => {
    const daysDataUpdate = [...(days || [])].reduce(
      (hash: BusinessHoursByDay, d) => {
        hash[d] =
          workHours === WorkHours.FullDay
            ? { startHour: DAY_START, stopHour: DAY_END }
            : { startHour, stopHour };
        return hash;
      },
      {},
    );
    if (!isEqual(daysDataUpdate, daysData)) {
      setDaysData(daysDataUpdate);
    }
  }, [days, daysData, workHours, startHour, stopHour]);

  return (
    <div className="day-and-time-picker">
      <div className="week-day-picker">
        {WEEK_DAYS.map((day, idx) => (
          <DayPickerButton
            key={`day-btn-${idx}`}
            idx={idx}
            day={day}
            handleToggle={() => handleToggle(idx)}
            isSelected={days.has(idx) || false}
            className="week-day-picker__day"
            useCheckboxStyle={useCheckboxStyle}
          />
        ))}
        <CheckBox
          className="week-day-picker__checkbox"
          isChecked={areAllChecked}
          onToggle={() =>
            areAllChecked
              ? setDays(new Set([]))
              : setDays(new Set(Array(7).keys()))
          }
        />
        Select all
      </div>

      <div className="day-and-time-picker__time-settings">
        <RadioInput
          className="day-and-time-picker__time-settings__input"
          name="workTime"
          checked={isFullDay}
          value="false"
          label="All day"
          onChange={onSelectFullDay}
        />
        <RadioInput
          className="day-and-time-picker__time-settings__input"
          name="workTime"
          checked={!isFullDay}
          value="true"
          label="Select time"
          onChange={onSelectByDay}
        />
      </div>
      {!isFullDay && (
        <div className="day-and-time-picker__time-dropdowns">
          <div>FROM</div>
          <TimePicker
            disabled={isFullDay}
            onChange={safelySetStartTime}
            value={startHour}
            hideIcon
          />
          <div>TO</div>
          <TimePicker
            disabled={isFullDay}
            onChange={safelySetEndTime}
            value={stopHour}
            hideIcon
          />
        </div>
      )}
    </div>
  );
};
